import { useState } from "react"

import { cn } from "@/lib/utils"
import { PersonalizedReportsCard } from "@/features/today"
import { useSubscriptionStore } from "@/stores/subscription-store"
import type { UserSubscription } from "@/features/subscriptions"

import BackArrow from "@/assets/img/profile/back_arrow.svg?react"
import GeneralBackground from "@/assets/img/profile/general_background.webp"
import PurpleCheck from "@/assets/img/profile/plan/purple_check.svg?react"

import { useActivePlan } from "../hooks/use-active-plan"

import { PlanRetentionModal } from "./plan-retention-modal"
import { PlanCanceledModal } from "./plan-canceled-modal"

interface MyPlanModalProps {
  onClose: () => void
}

/**
 * "My plan" screen: special-offers cross-sell + the user's active plan with the
 * cancel/retention flow. Opened from the profile menu. Ported from the Figma
 * "Unsubscribe" section.
 */
export function MyPlanModal({ onClose }: MyPlanModalProps) {
  const { isLoading: isFetching, subscription } = useActivePlan()
  const isCanceled = useSubscriptionStore((s) => s.isCanceled)
  const cancel = useSubscriptionStore((s) => s.cancel)

  const [showRetention, setShowRetention] = useState(false)
  const [showCanceled, setShowCanceled] = useState(false)
  const [isCanceling, setIsCanceling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)

  const hasPlan = subscription !== null
  const planName = derivePlanName(subscription)
  const activeUntilLabel = deriveActiveUntilLabel(subscription)
  const canCancel = !isFetching && !isCanceled && hasPlan

  const handleConfirmCancel = async () => {
    if (!subscription) return
    setIsCanceling(true)
    setCancelError(null)
    try {
      // Route through the store so the cancel path is single-sourced: it hits the
      // real /api/cancel in production and short-circuits under the dev bypass.
      const res = await cancel(subscription)
      if (res.ok) {
        setShowRetention(false)
        setShowCanceled(true)
      } else {
        setCancelError(res.error ?? "Something went wrong. Please try again.")
      }
    } catch {
      setCancelError("Something went wrong. Please try again.")
    } finally {
      setIsCanceling(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[1050] mx-auto max-h-[100dvh] w-full max-w-app overflow-y-auto bg-dark-base [scrollbar-width:none]">
      <img
        src={GeneralBackground}
        alt=""
        className="pointer-events-none absolute inset-x-0 top-0 h-60 w-full select-none object-cover"
      />

      <div className="relative">
        {/* Navbar */}
        <div className="relative flex h-14 items-center justify-center">
          <button
            type="button"
            onClick={onClose}
            aria-label="Back"
            className="absolute left-4 [&_svg]:size-6"
          >
            <BackArrow />
          </button>
          <h2 className="font-display text-[22px] font-medium leading-[30px] text-purple-light">
            My plan
          </h2>
        </div>

        <div className="flex flex-col gap-10 px-4 pb-8 pt-5">
          <div className="flex flex-col gap-8">
            {/* Intro */}
            <div className="flex flex-col items-center gap-2 text-center">
              <span className="rounded-full bg-green/20 px-3 py-2 font-body text-[10px] font-medium uppercase text-green">
                Our special offers
              </span>
              <h3 className="font-display text-2xl font-medium text-purple-light">
                Align with the stars <br /> and don’t miss these offers
              </h3>
              <p className="font-body text-base text-purple-light/70">
                Navigate love, destiny, and life’s twists with special deals tailored to bring
                clarity, guidance, and insights right when you need them most
              </p>
            </div>

            {/* Special offers card (shared with the Today screen) */}
            <PersonalizedReportsCard />
          </div>

          {/* My active plan */}
          <div className="flex flex-col items-center gap-4">
            <h3 className="font-display text-2xl font-medium text-purple-light">My active plan</h3>

            <div className="flex h-[84px] w-full items-center gap-6 rounded-2xl border border-purple-light/20 px-[15px] py-3">
              <div className="flex shrink-0 items-center gap-2">
                {isFetching ? (
                  <span className="size-6 shrink-0 animate-spin rounded-full border-2 border-purple-light/20 border-t-purple-light/50" />
                ) : hasPlan ? (
                  <PurpleCheck className="size-6 shrink-0" />
                ) : null}
                <span
                  className={cn(
                    "font-body text-base font-semibold",
                    isFetching || !hasPlan ? "text-purple-light/40" : "text-purple-light",
                  )}
                >
                  {isFetching ? "Fetching plans" : hasPlan ? planName : "No active plan"}
                </span>
              </div>

              <button
                type="button"
                onClick={() => setShowRetention(true)}
                disabled={!canCancel}
                className={cn(
                  "flex h-12 flex-1 items-center justify-center rounded-full font-body text-base font-semibold",
                  canCancel ? "bg-peach text-white" : "bg-purple-light/10 text-purple-light/20",
                )}
              >
                {isCanceled ? "Canceled" : "Cancel"}
              </button>
            </div>

            <p className="text-center font-body text-base text-purple-light/70">
              Canceling your membership will{" "}
              <span className="font-semibold text-peach">end your access</span> to exclusive
              astrological insights and personalized guidance that support your journey
            </p>
          </div>
        </div>
      </div>

      {showRetention && (
        <PlanRetentionModal
          isCanceling={isCanceling}
          error={cancelError}
          onStay={() => {
            setCancelError(null)
            setShowRetention(false)
          }}
          onConfirmCancel={handleConfirmCancel}
        />
      )}

      {showCanceled && (
        <PlanCanceledModal activeUntilLabel={activeUntilLabel} onContinue={onClose} />
      )}
    </div>
  )
}

type PlanInterval = "week" | "month" | "quarter" | "year"

const PLAN_NAME: Record<PlanInterval, string> = {
  week: "Weekly Plan",
  month: "Monthly Plan",
  quarter: "Quarterly Plan",
  year: "Yearly Plan",
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

/** Best-effort billing interval from the opaque plan code (no interval field in the API). */
function detectInterval(planCode: string | undefined): PlanInterval | null {
  const code = (planCode ?? "").toLowerCase()
  if (!code) return null
  if (/year|annual|12\s*m|365/.test(code)) return "year"
  if (/quarter|3\s*m|90\s*day/.test(code)) return "quarter"
  if (/month|1\s*m|30\s*day/.test(code)) return "month"
  if (/week|wk|7\s*day/.test(code)) return "week"
  return null
}

function derivePlanName(subscription: UserSubscription | null): string {
  const interval = detectInterval(subscription?.planCode)
  return interval ? PLAN_NAME[interval] : "Your Plan"
}

function advance(date: Date, interval: PlanInterval): void {
  switch (interval) {
    case "week":
      date.setDate(date.getDate() + 7)
      break
    case "month":
      date.setMonth(date.getMonth() + 1)
      break
    case "quarter":
      date.setMonth(date.getMonth() + 3)
      break
    case "year":
      date.setFullYear(date.getFullYear() + 1)
      break
  }
}

function ordinalSuffix(day: number): string {
  const rem100 = day % 100
  if (rem100 >= 11 && rem100 <= 13) return "th"
  switch (day % 10) {
    case 1:
      return "st"
    case 2:
      return "nd"
    case 3:
      return "rd"
    default:
      return "th"
  }
}

/**
 * "14th May, 2024" — the current billing period's end date, i.e. how long access
 * lasts after canceling. Null when we can't derive it (missing date/interval), so the
 * canceled popover can fall back to a generic phrasing instead of a wrong date.
 */
function deriveActiveUntilLabel(subscription: UserSubscription | null): string | null {
  const interval = detectInterval(subscription?.planCode)
  if (!subscription?.startedAt || !interval) return null

  const start = new Date(subscription.startedAt)
  if (Number.isNaN(start.getTime())) return null

  const periodEnd = new Date(start)
  const now = new Date()
  let guard = 0
  while (periodEnd.getTime() <= now.getTime() && guard < 1200) {
    advance(periodEnd, interval)
    guard += 1
  }

  const day = periodEnd.getDate()
  return `${day}${ordinalSuffix(day)} ${MONTHS[periodEnd.getMonth()]}, ${periodEnd.getFullYear()}`
}
